import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Input,
  Link as JoyLink,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowLeft as BackIcon,
  CaretDown as ExpandIcon,
  CaretUp as CollapseIcon,
  CheckCircle as SuccessIcon,
  CreditCard as CardIcon,
  MagicWand as PopulateIcon,
  Minus as MinusIcon,
  Plus as PlusIcon,
  Question as HelpIcon,
  Trash as RemoveIcon,
} from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { ResponsiveTable, type ResponsiveColumn } from '../common/ResponsiveTable';
import type { BacklinkGrade, BacklinkOrder, BacklinkOrderItem } from '../../types/marketing';
import type { TopPage } from '../../types';

interface OrderRow {
  key: string;
  gradeId: number;
  url: string;
  anchor_text: string;
  general_niche: string;
  specific_niche: string;
}

interface PaymentMethodInfo {
  payment_method: {
    id: string;
    type: string;
    card: { brand: string; last4: string; exp_month: number; exp_year: number } | null;
  } | null;
}

const money = (value: string | number) => `$${Number(value).toFixed(2)}`;
const MAX_PER_GRADE = 50;

/** Stylized "screenshot" of a backlink placed naturally inside an article. */
const PlacementIllustration: React.FC = () => (
  <svg viewBox="0 0 460 250" width="100%" role="img" aria-label="Example of a backlink placed naturally inside an article">
    <rect x="1" y="1" width="458" height="248" rx="10" fill="var(--joy-palette-background-surface)" stroke="var(--joy-palette-neutral-300)" />
    <rect x="1" y="1" width="458" height="34" rx="10" fill="var(--joy-palette-neutral-100)" stroke="var(--joy-palette-neutral-300)" />
    <circle cx="20" cy="18" r="5" fill="var(--joy-palette-neutral-300)" />
    <circle cx="36" cy="18" r="5" fill="var(--joy-palette-neutral-300)" />
    <circle cx="52" cy="18" r="5" fill="var(--joy-palette-neutral-300)" />
    <rect x="70" y="9" width="290" height="18" rx="9" fill="var(--joy-palette-background-body)" stroke="var(--joy-palette-neutral-300)" />
    <text x="82" y="22" fontSize="10" fill="var(--joy-palette-text-tertiary)" fontFamily="monospace">
      established-home-blog.com/roof-maintenance-guide
    </text>
    {/* Article headline + meta */}
    <rect x="28" y="54" width="300" height="14" rx="7" fill="var(--joy-palette-neutral-400)" />
    <rect x="28" y="76" width="140" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    {/* Paragraph lines */}
    <rect x="28" y="100" width="404" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    <rect x="28" y="116" width="380" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    <rect x="28" y="132" width="404" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    {/* The placed anchor link inside a sentence */}
    <rect x="28" y="148" width="96" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    <rect x="132" y="146" width="150" height="13" rx="6" fill="var(--joy-palette-primary-softBg)" />
    <text x="140" y="156" fontSize="10" fontWeight="600" fill="var(--joy-palette-primary-600)" textDecoration="underline">
      roofing company in Scranton
    </text>
    <rect x="290" y="148" width="142" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    <rect x="28" y="164" width="360" height="8" rx="4" fill="var(--joy-palette-neutral-200)" />
    {/* Callout */}
    <path d="M207 200 L207 168" stroke="var(--joy-palette-primary-500)" strokeWidth="1.5" strokeDasharray="3 3" />
    <rect x="96" y="200" width="222" height="26" rx="13" fill="var(--joy-palette-primary-500)" />
    <text x="207" y="217" fontSize="11" fontWeight="600" fill="#fff" textAnchor="middle">
      Your anchor text, woven in naturally
    </text>
  </svg>
);

/** Stylized "screenshot" of a completed order with published target domains. */
const CompletedOrderIllustration: React.FC = () => (
  <svg viewBox="0 0 460 250" width="100%" role="img" aria-label="Example of a completed order listing published backlinks">
    <rect x="1" y="1" width="458" height="248" rx="10" fill="var(--joy-palette-background-surface)" stroke="var(--joy-palette-neutral-300)" />
    <text x="24" y="38" fontSize="13" fontWeight="700" fontFamily="monospace" fill="var(--joy-palette-text-primary)">
      APXBL117631745415
    </text>
    <rect x="196" y="24" width="82" height="20" rx="10" fill="var(--joy-palette-success-softBg)" />
    <text x="237" y="38" fontSize="11" fontWeight="600" fill="var(--joy-palette-success-700, var(--joy-palette-success-600))" textAnchor="middle">
      Complete
    </text>
    {/* Table header */}
    <rect x="16" y="58" width="428" height="26" rx="6" fill="var(--joy-palette-neutral-100)" />
    <text x="28" y="75" fontSize="10" fontWeight="700" fill="var(--joy-palette-text-secondary)">YOUR PAGE</text>
    <text x="190" y="75" fontSize="10" fontWeight="700" fill="var(--joy-palette-text-secondary)">TARGET DOMAIN</text>
    <text x="392" y="75" fontSize="10" fontWeight="700" fill="var(--joy-palette-text-secondary)">TYPE</text>
    {/* Row 1 */}
    <text x="28" y="106" fontSize="10" fill="var(--joy-palette-text-primary)">dundersons.com/roof-repair</text>
    <text x="190" y="106" fontSize="10" fill="var(--joy-palette-primary-600)" textDecoration="underline">
      home-improvement-weekly.com/…
    </text>
    <text x="392" y="106" fontSize="10" fill="var(--joy-palette-text-primary)">Grade Five</text>
    <line x1="16" y1="120" x2="444" y2="120" stroke="var(--joy-palette-neutral-200)" />
    {/* Row 2 */}
    <text x="28" y="142" fontSize="10" fill="var(--joy-palette-text-primary)">dundersons.com/storm-damage</text>
    <text x="190" y="142" fontSize="10" fill="var(--joy-palette-primary-600)" textDecoration="underline">
      contractor-insights-blog.com/…
    </text>
    <text x="392" y="142" fontSize="10" fill="var(--joy-palette-text-primary)">Grade Four</text>
    <line x1="16" y1="156" x2="444" y2="156" stroke="var(--joy-palette-neutral-200)" />
    {/* Row 3 */}
    <text x="28" y="178" fontSize="10" fill="var(--joy-palette-text-primary)">dundersons.com/areas-we-serve</text>
    <text x="190" y="178" fontSize="10" fill="var(--joy-palette-primary-600)" textDecoration="underline">
      pa-homeowner-guide.com/…
    </text>
    <text x="392" y="178" fontSize="10" fill="var(--joy-palette-text-primary)">Grade Four</text>
    {/* Footer note */}
    <rect x="16" y="200" width="428" height="30" rx="8" fill="var(--joy-palette-neutral-50, var(--joy-palette-neutral-100))" />
    <text x="230" y="219" fontSize="10" fill="var(--joy-palette-text-tertiary)" textAnchor="middle">
      Every published placement is listed in your order as it goes live
    </text>
  </svg>
);

const HowCurationWorksModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <ModalDialog sx={{ maxWidth: 780, maxHeight: '92vh', overflow: 'auto' }}>
      <ModalClose />
      <Typography level="h4">How our curated backlinks work</Typography>
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {[
          ['1. You place the order', 'Choose how many links you want at each grade and which of your pages they should point to — we pre-fill your Top Pages so the highest-value targets are covered first.'],
          ['2. We research real articles', 'Our team hand-picks established, topically-relevant articles on aged websites with genuine traffic — never link farms, directories, or automated networks.'],
          ['3. Links are placed naturally', 'A writer works your anchor text into the existing article so the mention reads like a genuine editorial reference. Higher grades mean stronger sites: more referring domains (RD) and higher domain authority (DA).'],
          ['4. You get the receipts', "As each placement is published (typically within 14 days), its live URL appears in your order below — so you can see exactly where every link lives."],
        ].map(([title, body]) => (
          <Box key={title}>
            <Typography level="title-sm">{title}</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>{body}</Typography>
          </Box>
        ))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 1,
          }}
        >
          <Box>
            <PlacementIllustration />
            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', mt: 0.5 }}>
              Illustrative example — a placement inside an established article
            </Typography>
          </Box>
          <Box>
            <CompletedOrderIllustration />
            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', mt: 0.5 }}>
              Illustrative example — a completed order with published links
            </Typography>
          </Box>
        </Box>
      </Stack>
    </ModalDialog>
  </Modal>
);

export const BacklinksTable: React.FC = () => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<'build' | 'details'>('build');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [formError, setFormError] = useState('');
  const [placedReference, setPlacedReference] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // When set, the details step is editing this saved draft instead of a new order
  const [draftId, setDraftId] = useState<number | null>(null);
  const [draftSaved, setDraftSaved] = useState(false);

  const { data: grades = [], isLoading: gradesLoading } = useQuery({
    queryKey: ['backlink-grades'],
    queryFn: async () => {
      const response = await api.get<any>('/api/marketing/backlink-grades/');
      const data = response.data;
      return ((data.results || data) as BacklinkGrade[]).filter((g) => g.is_active);
    },
  });

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['backlink-orders'],
    queryFn: async () => {
      const response = await api.get<any>('/api/marketing/backlink-orders/');
      const data = response.data;
      return (data.results || data) as BacklinkOrder[];
    },
  });

  const { data: topPages = [] } = useQuery({
    queryKey: ['top-pages'],
    queryFn: async () => {
      const response = await api.get<any>('/api/top-pages/');
      const data = response.data;
      return (data.results || data) as TopPage[];
    },
  });

  const { data: paymentData } = useQuery({
    queryKey: ['default-payment-method'],
    queryFn: async () => {
      const response = await api.get<PaymentMethodInfo>('/api/subscriptions/payment-method/');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const card = paymentData?.payment_method?.card ?? null;

  const toItemsPayload = (orderRows: OrderRow[]) =>
    orderRows.map((row) => ({
      grade_id: row.gradeId,
      url: row.url.trim(),
      anchor_text: row.anchor_text.trim(),
      general_niche: row.general_niche.trim(),
      specific_niche: row.specific_niche.trim(),
    }));

  const placeOrder = useMutation({
    mutationFn: async (orderRows: OrderRow[]) => {
      // Placing a resumed draft syncs the latest edits and pays for that
      // order (keeping its reference); otherwise a new order is created.
      const response = draftId
        ? await api.post<BacklinkOrder>(`/api/marketing/backlink-orders/${draftId}/place/`, {
            items: toItemsPayload(orderRows),
          })
        : await api.post<BacklinkOrder>('/api/marketing/backlink-orders/', {
            items: toItemsPayload(orderRows),
          });
      return response.data;
    },
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['backlink-orders'] });
      setPlacedReference(order.reference);
      setQuantities({});
      setRows([]);
      setFormError('');
      setDraftId(null);
      setStep('build');
    },
    onError: (err: any) => {
      setFormError(
        err.response?.data?.error ||
        err.response?.data?.items?.[0] ||
        'Failed to place the order. Please try again.'
      );
    },
  });

  const saveDraft = useMutation({
    mutationFn: async (orderRows: OrderRow[]) => {
      const response = draftId
        ? await api.patch<BacklinkOrder>(`/api/marketing/backlink-orders/${draftId}/`, {
            items: toItemsPayload(orderRows),
          })
        : await api.post<BacklinkOrder>('/api/marketing/backlink-orders/', {
            items: toItemsPayload(orderRows),
            is_draft: true,
          });
      return response.data;
    },
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['backlink-orders'] });
      setDraftId(order.id);
      setDraftSaved(true);
      setFormError('');
      setTimeout(() => setDraftSaved(false), 3000);
    },
    onError: (err: any) => {
      setFormError(
        err.response?.data?.error ||
        err.response?.data?.items?.[0] ||
        'Failed to save the draft. Please try again.'
      );
    },
  });

  const deleteDraft = useMutation({
    mutationFn: async (orderId: number) => {
      await api.delete(`/api/marketing/backlink-orders/${orderId}/`);
      return orderId;
    },
    onSuccess: (orderId) => {
      queryClient.invalidateQueries({ queryKey: ['backlink-orders'] });
      if (draftId === orderId) setDraftId(null);
    },
  });

  const resumeDraft = (order: BacklinkOrder) => {
    const nextQuantities: Record<number, number> = {};
    order.items.forEach((item) => {
      nextQuantities[item.grade.id] = (nextQuantities[item.grade.id] || 0) + 1;
    });
    setQuantities(nextQuantities);
    setRows(
      order.items.map((item) => ({
        key: `saved-${item.id}`,
        gradeId: item.grade.id,
        url: item.url,
        anchor_text: item.anchor_text,
        general_niche: item.general_niche,
        specific_niche: item.specific_niche,
      }))
    );
    setDraftId(order.id);
    setPlacedReference('');
    setFormError('');
    setStep('details');
  };

  const totalLinks = grades.reduce((sum, g) => sum + (quantities[g.id] || 0), 0);
  const totalCost = grades.reduce((sum, g) => sum + (quantities[g.id] || 0) * Number(g.price), 0);

  const adjustQuantity = (gradeId: number, delta: number) => {
    setQuantities((prev) => {
      const next = Math.min(MAX_PER_GRADE, Math.max(0, (prev[gradeId] || 0) + delta));
      return { ...prev, [gradeId]: next };
    });
  };

  // Build one empty row per ordered link. Rows the user already edited
  // (matched by grade + position) survive a round-trip back to the quantity
  // step. URLs/anchors fill on demand via "Populate from Top Pages".
  const startBuilding = () => {
    setRows((prev) => {
      const next: OrderRow[] = [];
      let globalIndex = 0;
      for (const grade of grades) {
        const qty = quantities[grade.id] || 0;
        const previousForGrade = prev.filter((r) => r.gradeId === grade.id);
        for (let i = 0; i < qty; i++) {
          if (previousForGrade[i]) {
            next.push(previousForGrade[i]);
          } else {
            next.push({
              key: `${grade.id}-${i}-${globalIndex}`,
              gradeId: grade.id,
              url: '',
              anchor_text: '',
              general_niche: 'Construction',
              specific_niche: 'Roofing',
            });
          }
          globalIndex += 1;
        }
      }
      return next;
    });
    setFormError('');
    setStep('details');
  };

  // Fill every row's URL + anchor from the org's Top Pages, cycling through
  // the list when there are more links than pages.
  const populateFromTopPages = () => {
    if (topPages.length === 0) return;
    setRows((prev) =>
      prev.map((row, index) => {
        const topPage = topPages[index % topPages.length];
        return { ...row, url: topPage?.url || '', anchor_text: topPage?.keyword_1 || '' };
      })
    );
    setFormError('');
  };

  const updateRow = (key: string, field: keyof OrderRow, value: string) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  const removeRow = (row: OrderRow) => {
    setRows((prev) => prev.filter((r) => r.key !== row.key));
    adjustQuantity(row.gradeId, -1);
  };

  const handlePlaceOrder = () => {
    if (rows.some((row) => !row.url.trim())) {
      setFormError('Every link needs a destination URL. Fill in the missing URLs or remove those rows.');
      return;
    }
    setFormError('');
    placeOrder.mutate(rows);
  };

  const gradeById = (id: number) => grades.find((g) => g.id === id);

  // ---------------------------------------------------------------- builder
  const builderColumns: ResponsiveColumn<BacklinkGrade>[] = [
    {
      key: 'grade', header: 'Grade', mobileVariant: 'header',
      render: (g) => <Typography level="title-sm">{g.name}</Typography>,
    },
    {
      key: 'rd', header: 'RD', width: '18%',
      render: (g) => (
        <Typography level="body-sm">
          {g.rd_display}
          {g.traffic_note && (
            <Typography level="body-xs" sx={{ color: 'primary.500', ml: 0.5 }} component="span">
              ({g.traffic_note})
            </Typography>
          )}
        </Typography>
      ),
    },
    {
      key: 'da', header: 'Average DA', width: '13%',
      render: (g) => <Typography level="body-sm">{g.da_display}</Typography>,
    },
    {
      key: 'cost', header: 'Cost', width: '12%',
      sortValue: (row) => Number(row.price),
      render: (g) => <Typography level="body-sm">{money(g.price)}</Typography>,
    },
    {
      key: 'qty', header: 'Quantity', width: '16%',
      render: (g) => {
        const qty = quantities[g.id] || 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="sm" variant="solid" color="primary"
              disabled={qty === 0}
              onClick={() => adjustQuantity(g.id, -1)}
              aria-label={`Remove one ${g.name} link`}
            >
              <MinusIcon />
            </IconButton>
            <Typography level="body-sm" sx={{ minWidth: 24, textAlign: 'center' }}>{qty}</Typography>
            <IconButton
              size="sm" variant="solid" color="primary"
              onClick={() => adjustQuantity(g.id, 1)}
              aria-label={`Add one ${g.name} link`}
            >
              <PlusIcon />
            </IconButton>
          </Box>
        );
      },
    },
    {
      key: 'total', header: 'Total', width: '12%',
      render: (g) => (
        <Typography level="body-sm" fontWeight="lg">
          {money((quantities[g.id] || 0) * Number(g.price))}
        </Typography>
      ),
    },
  ];

  // ---------------------------------------------------------------- details
  const detailColumns: ResponsiveColumn<OrderRow>[] = [
    {
      key: 'grade', header: 'RD', width: '10%', mobileVariant: 'header',
      render: (row) => {
        const grade = gradeById(row.gradeId);
        return <Chip size="sm" variant="soft" color="primary">{grade?.rd_display || ''}</Chip>;
      },
    },
    {
      key: 'url', header: 'URL', width: '32%',
      sortValue: (row) => row.url,
      render: (row) => (
        <Input
          size="sm"
          value={row.url}
          placeholder="https://yourwebsite.com/page"
          error={!row.url.trim()}
          onChange={(e) => updateRow(row.key, 'url', e.target.value)}
        />
      ),
    },
    {
      key: 'anchor', header: 'Anchor Text', width: '20%',
      sortValue: (row) => row.anchor_text,
      render: (row) => (
        <Input
          size="sm"
          value={row.anchor_text}
          placeholder="e.g. roofing company in Scranton"
          onChange={(e) => updateRow(row.key, 'anchor_text', e.target.value)}
        />
      ),
    },
    {
      key: 'general', header: 'General Niche', width: '15%',
      sortValue: (row) => row.general_niche,
      render: (row) => (
        <Input
          size="sm"
          value={row.general_niche}
          onChange={(e) => updateRow(row.key, 'general_niche', e.target.value)}
        />
      ),
    },
    {
      key: 'specific', header: 'Specific Niche', width: '15%',
      sortValue: (row) => row.specific_niche,
      render: (row) => (
        <Input
          size="sm"
          value={row.specific_niche}
          onChange={(e) => updateRow(row.key, 'specific_niche', e.target.value)}
        />
      ),
    },
  ];

  // ------------------------------------------------------------- order list
  const itemColumns: ResponsiveColumn<BacklinkOrderItem>[] = [
    {
      key: 'url', header: 'URL', mobileVariant: 'header',
      sortValue: (row) => row.url,
      render: (item) => (
        <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{item.url}</Typography>
      ),
    },
    {
      key: 'anchor', header: 'Anchor',
      sortValue: (row) => row.anchor_text,
      render: (item) => <Typography level="body-sm">{item.anchor_text || '-'}</Typography>,
    },
    {
      key: 'general', header: 'General Niche', hideOnMobile: true,
      sortValue: (row) => row.general_niche,
      render: (item) => <Typography level="body-sm">{item.general_niche || '-'}</Typography>,
    },
    {
      key: 'specific', header: 'Specific Niche',
      sortValue: (row) => row.specific_niche,
      render: (item) => <Typography level="body-sm">{item.specific_niche || '-'}</Typography>,
    },
    {
      key: 'target', header: 'Target Domain', mobileVariant: 'body',
      sortValue: (row) => row.target_domain,
      render: (item) => item.target_domain ? (
        <JoyLink
          href={item.target_domain}
          target="_blank"
          rel="noopener noreferrer"
          level="body-sm"
          sx={{ overflowWrap: 'anywhere' }}
        >
          {item.target_domain}
        </JoyLink>
      ) : (
        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Pending placement</Typography>
      ),
    },
    {
      key: 'type', header: 'Type',
      render: (item) => <Typography level="body-sm">Grade {item.grade.name} Link</Typography>,
    },
    {
      key: 'rd', header: 'RD', hideOnMobile: true,
      render: (item) => <Typography level="body-sm">{item.grade.rd_display}</Typography>,
    },
    {
      key: 'da', header: 'Avg DA', hideOnMobile: true,
      render: (item) => <Typography level="body-sm">{item.grade.da_display}</Typography>,
    },
  ];

  if (gradesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <HowCurationWorksModal open={showHowItWorks} onClose={() => setShowHowItWorks(false)} />
      {placedReference && (
        <Alert
          color="success"
          startDecorator={<SuccessIcon />}
          endDecorator={
            <Button variant="plain" color="success" size="sm" onClick={() => setPlacedReference('')}>
              Dismiss
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Order {placedReference} placed! Our team is building your backlinks — each placement
          appears under Your Orders as it's published (typically within 14 days).
        </Alert>
      )}

      {step === 'build' && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="h3">Curated Backlinks</Typography>
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={() => setShowHowItWorks(true)}
                aria-label="How our curated backlinks work"
                sx={{ borderRadius: '50%' }}
              >
                <HelpIcon />
              </IconButton>
            </Box>
            <Typography level="body-sm" sx={{ color: 'text.secondary', maxWidth: 720 }}>
              Boost your website's authority and keyword rankings with hand-curated backlinks
              placed inside established, high-quality articles. Pick how many links you want at
              each grade — higher grades come from stronger sites with more referring domains (RD)
              and higher domain authority (DA).
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
            <ResponsiveTable rows={grades} getRowKey={(g) => g.id} columns={builderColumns} />
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 3, mt: 2 }}>
            <Typography level="title-md">
              {totalLinks} link{totalLinks === 1 ? '' : 's'} — {money(totalCost)}
            </Typography>
            <Button size="lg" disabled={totalLinks === 0} onClick={startBuilding}>
              Start Building
            </Button>
          </Box>
        </>
      )}

      {step === 'details' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="plain" startDecorator={<BackIcon />} onClick={() => setStep('build')}>
              Adjust quantities
            </Button>
            <Typography level="h3">Supply your order details</Typography>
            {draftId && (
              <Chip size="sm" variant="soft" color="neutral">
                Editing draft{orders.find((o) => o.id === draftId)?.reference ? ` ${orders.find((o) => o.id === draftId)!.reference}` : ''}
              </Chip>
            )}
            <Button
              variant="soft"
              color="primary"
              startDecorator={<PopulateIcon />}
              disabled={topPages.length === 0}
              onClick={populateFromTopPages}
              sx={{ ml: 'auto' }}
            >
              Populate from Top Pages
            </Button>
          </Box>

          {topPages.length === 0 && (
            <Alert color="warning" sx={{ mb: 2 }}>
              <Typography level="body-sm">
                Populate from Top Pages needs Top Pages set on your organization. Add them under{' '}
                <JoyLink href="/settings/organization" sx={{ fontSize: 'inherit' }}>
                  Organization Settings
                </JoyLink>{' '}
                — or fill in the URLs below manually.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
              <ResponsiveTable
                rows={rows}
                getRowKey={(row) => row.key}
                columns={detailColumns}
                rowActions={(row) => (
                  <IconButton
                    size="sm" variant="plain" color="danger"
                    onClick={() => removeRow(row)}
                    aria-label="Remove this link"
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
                emptyState={
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    No links in this order — go back and pick quantities.
                  </Typography>
                }
              />
            </Card>

            <Card variant="outlined">
              <Typography level="title-lg">Order Summary</Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {grades
                  .filter((g) => rows.some((row) => row.gradeId === g.id))
                  .map((g) => {
                    const count = rows.filter((row) => row.gradeId === g.id).length;
                    return (
                      <Box key={g.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">
                          Grade {g.name} × {count}
                        </Typography>
                        <Typography level="body-sm">{money(count * Number(g.price))}</Typography>
                      </Box>
                    );
                  })}
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography level="title-md">Total</Typography>
                  <Typography level="title-md" color="success">
                    {money(rows.reduce((sum, row) => sum + Number(gradeById(row.gradeId)?.price || 0), 0))}
                  </Typography>
                </Box>

                {card ? (
                  <Typography level="body-sm" startDecorator={<CardIcon />} sx={{ color: 'text.secondary' }}>
                    Charged to {card.brand.toUpperCase()} •••• {card.last4}
                  </Typography>
                ) : (
                  <Alert color="warning" size="sm">
                    {/* Single Typography so the sentence flows inline — bare
                        children become separate flex items inside Alert */}
                    <Typography level="body-sm">
                      No card on file — add one under{' '}
                      <JoyLink href="/settings/billing" sx={{ fontSize: 'inherit' }}>
                        Billing Settings
                      </JoyLink>{' '}
                      first.
                    </Typography>
                  </Alert>
                )}

                {formError && <Alert color="danger" size="sm">{formError}</Alert>}

                <Button
                  size="lg"
                  loading={placeOrder.isPending}
                  disabled={rows.length === 0 || saveDraft.isPending}
                  onClick={handlePlaceOrder}
                >
                  Place Order
                </Button>
                <Button
                  variant="outlined"
                  color={draftSaved ? 'success' : 'neutral'}
                  loading={saveDraft.isPending}
                  disabled={rows.length === 0 || placeOrder.isPending}
                  onClick={() => saveDraft.mutate(rows)}
                >
                  {draftSaved ? 'Draft Saved' : 'Save Draft'}
                </Button>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Your saved billing card is charged immediately. Links are researched, written,
                  and placed by our team — typical turnaround is 14 days.
                </Typography>
              </Stack>
            </Card>
          </Box>
        </>
      )}

      {/* ------------------------------------------------ order history */}
      <Divider sx={{ my: 4 }} />
      <Typography level="h4" sx={{ mb: 1 }}>Your Orders</Typography>
      {ordersLoading ? (
        <CircularProgress size="sm" />
      ) : orders.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          No backlink orders yet. Your orders and their published links will appear here.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {orders.map((order) => {
            const expanded = expandedOrderId === order.id;
            return (
              <Card key={order.id} variant="outlined" sx={{ p: 0 }}>
                <Box
                  onClick={() => setExpandedOrderId(expanded ? null : order.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
                    cursor: 'pointer', flexWrap: 'wrap',
                    '&:hover': { bgcolor: 'neutral.softHoverBg' },
                  }}
                >
                  <Typography level="title-sm" sx={{ fontFamily: 'monospace' }}>
                    {order.reference}
                  </Typography>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={
                      order.status === 'complete' ? 'success'
                      : order.status === 'draft' ? 'neutral'
                      : 'warning'
                    }
                  >
                    {order.status === 'complete' ? 'Complete' : order.status === 'draft' ? 'Draft' : 'In Process'}
                  </Chip>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {new Date(order.created_at).toLocaleDateString()}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    {order.items.length} link{order.items.length === 1 ? '' : 's'}
                  </Typography>
                  <Typography level="title-sm" sx={{ ml: 'auto' }}>
                    {money(order.total_amount)}
                  </Typography>
                  {order.status === 'draft' && (
                    <>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); resumeDraft(order); }}
                      >
                        Resume
                      </Button>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        aria-label="Delete draft"
                        loading={deleteDraft.isPending && deleteDraft.variables === order.id}
                        onClick={(e) => { e.stopPropagation(); deleteDraft.mutate(order.id); }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </>
                  )}
                  {expanded ? <CollapseIcon /> : <ExpandIcon />}
                </Box>
                {expanded && (
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <ResponsiveTable rows={order.items} getRowKey={(item) => item.id} columns={itemColumns} />
                  </Box>
                )}
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
